import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { CSSProperties, HTMLProps, ReactNode, RefObject, useRef } from 'react';

import useOnClickOutside from '../../hooks/input/useOnClickOutside';

import styles from './TopRightPopOver.module.css';

type TopRightPopOverProps = {
    width: CSSProperties['width'];
    isOpen: boolean;
    children: ReactNode;
    noPadding?: boolean;
    onClickOutside?: () => void;
    onClick?: HTMLProps<HTMLDivElement>['onClick'];
    clickOutsideRef?: RefObject<HTMLElement>;
};

const TopRightPopOver = ({
    width,
    isOpen,
    children,
    noPadding,
    onClickOutside,
    onClick,
    clickOutsideRef,
}: TopRightPopOverProps) => {
    const localRef = useRef<HTMLDivElement>(null);
    const ref = clickOutsideRef || localRef;

    useOnClickOutside(ref, () => {
        if (isOpen && onClickOutside) {
            onClickOutside();
        }
    });

    return (
        <div className={styles.Root} ref={localRef}>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: {
                                scale: 1,
                                opacity: 1,
                                transformOrigin: 'top right',
                            },
                            collapsed: {
                                scale: 0.7,
                                opacity: 0,
                                transformOrigin: 'top right',
                            },
                        }}
                        transition={{
                            duration: 0.08,
                            ease: 'easeOut',
                        }}
                    >
                        <div
                            style={{ width: width, overflow: 'hidden' }}
                            onClick={onClick}
                            className={classNames(styles.Panel, {
                                [styles.Panel_isOpen]: isOpen,
                                [styles.Panel_noPadding]: noPadding,
                            })}
                        >
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TopRightPopOver;
